import React, { createContext, useContext, useState, useEffect } from 'react';
import { DEFAULT_LANGUAGE, getBrowserLanguage, translations } from '../utils/languages';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Check URL parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    
    // Available languages
    const availableLanguages = ['en', 'ja', 'zh-TW'];
    
    // If URL parameter is valid, use it
    if (langParam && availableLanguages.includes(langParam)) {
      localStorage.setItem('tarot-language', langParam);
      return langParam;
    }
    
    // Get saved language or browser preference
    const saved = localStorage.getItem('tarot-language');
    return saved || getBrowserLanguage();
  });

  // Translation function
  const t = (key, options = {}) => {
    // Check current language first
    if (translations[currentLanguage]?.[key]) {
      return translations[currentLanguage][key];
    }
    
    // If not found and fallback is provided, try the fallback
    if (options.fallback !== undefined) {
      return options.fallback;
    }
    
    // Try default language
    if (translations[DEFAULT_LANGUAGE]?.[key]) {
      return translations[DEFAULT_LANGUAGE][key];
    }
    
    // Last resort, return the key itself
    return key;
  };

  // Change language function
  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode);
    localStorage.setItem('tarot-language', langCode);
    document.documentElement.lang = langCode;
    
    // Update URL parameter
    const url = new URL(window.location);
    url.searchParams.set('lang', langCode);
    window.history.replaceState({}, '', url);
  };

  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  // Listen for URL parameter changes (for browser navigation)
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const langParam = urlParams.get('lang');
      const availableLanguages = ['en', 'ja', 'zh-TW'];
      
      if (langParam && availableLanguages.includes(langParam) && langParam !== currentLanguage) {
        setCurrentLanguage(langParam);
        localStorage.setItem('tarot-language', langParam);
        document.documentElement.lang = langParam;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentLanguage]);

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    isRTL: false // None of our languages need RTL support
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};